/**
 * WaitingRoom Component
 * 
 * Patient waiting room before the provider joins.
 * Shows appointment info and polls for provider status.
 * 
 * Premium healthcare design with refined waiting state and appointment info.
 */

import React, { useEffect, useState, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { 
  Clock, 
  User, 
  Building2, 
  Loader2,
  CheckCircle2,
  AlertCircle,
  Video,
  RefreshCw,
  FileText,
  Sparkles
} from 'lucide-react'
import { telehealthService, WaitingRoomStatus } from '@/services/telehealthService'
import DeviceCheck from './DeviceCheck'
import RecordingConsentModal, { hasRecordingConsent } from './RecordingConsentModal'
import { cn } from '@/lib/utils'

interface WaitingRoomProps {
  appointmentId: string
  joinToken: string
  onProviderReady: () => void
  onError?: (error: string) => void
  onLeave?: () => void
}

const DEVICE_CHECK_KEY = 'novamed_device_check_passed'
const AUTO_JOIN_DELAY = 3

const WaitingRoom: React.FC<WaitingRoomProps> = ({
  appointmentId,
  joinToken,
  onProviderReady,
  onError,
  onLeave
}) => {
  const [status, setStatus] = useState<WaitingRoomStatus | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  const previouslyPassed = localStorage.getItem(DEVICE_CHECK_KEY) === 'true'
  const [deviceCheckPassed, setDeviceCheckPassed] = useState(previouslyPassed)
  const [showDeviceCheck, setShowDeviceCheck] = useState(!previouslyPassed)
  
  const [autoJoinCountdown, setAutoJoinCountdown] = useState<number | null>(null)
  
  const [hasConsent, setHasConsent] = useState(hasRecordingConsent())
  const [showConsentModal, setShowConsentModal] = useState(false)
  const [transcriptionEnabled] = useState(true)

  const checkStatus = useCallback(async () => {
    try {
      const waitingStatus = await telehealthService.checkWaitingRoom(appointmentId, joinToken)
      setStatus(waitingStatus)
      setError(null)

      if (waitingStatus.canJoin && deviceCheckPassed && autoJoinCountdown === null) {
        setAutoJoinCountdown(AUTO_JOIN_DELAY)
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to check status'
      setError(errorMessage)
      onError?.(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }, [appointmentId, joinToken, deviceCheckPassed, autoJoinCountdown, onError])

  useEffect(() => {
    checkStatus()
    
    const pollInterval = deviceCheckPassed ? 2000 : 5000
    const interval = setInterval(checkStatus, pollInterval)
    
    return () => clearInterval(interval)
  }, [checkStatus, deviceCheckPassed])

  useEffect(() => {
    if (autoJoinCountdown === null) return
    
    if (autoJoinCountdown <= 0) {
      if (!hasConsent && transcriptionEnabled) {
        setShowConsentModal(true)
        setAutoJoinCountdown(null)
        return
      }
      onProviderReady()
      return
    }
    
    const timer = setTimeout(() => {
      setAutoJoinCountdown(prev => prev !== null ? prev - 1 : null)
    }, 1000)
    
    return () => clearTimeout(timer)
  }, [autoJoinCountdown, onProviderReady, hasConsent, transcriptionEnabled])

  const handleConsentGiven = () => {
    setHasConsent(true)
    setShowConsentModal(false)
    onProviderReady()
  }

  const handleConsentDeclined = () => {
    setShowConsentModal(false)
    onLeave?.()
  }

  const handleJoinClick = () => {
    if (!hasConsent && transcriptionEnabled) {
      setShowConsentModal(true)
    } else {
      onProviderReady()
    }
  }

  const handleDeviceCheckComplete = () => {
    localStorage.setItem(DEVICE_CHECK_KEY, 'true')
    setDeviceCheckPassed(true)
    setShowDeviceCheck(false)
  }

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    })
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-[rgb(10_15_20)]">
        <img src="/nimbus_logo_white.svg" alt="Nimbbus Providers" className="h-9 mb-10" />
        <Loader2 className="h-10 w-10 animate-spin text-brand-teal mb-4" />
        <p className="text-base text-white font-medium">Loading appointment...</p>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-[rgb(10_15_20)] p-4">
        <img src="/nimbus_logo_white.svg" alt="Nimbbus Providers" className="h-9 mb-10" />
        <div className="max-w-sm w-full bg-[#141c24] rounded-2xl border border-slate-700/50 p-6">
          <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
            <AlertCircle className="h-7 w-7 text-red-400" />
          </div>
          <h2 className="text-lg font-semibold mb-2 text-white text-center">Unable to Load Appointment</h2>
          <p className="text-slate-400 text-center text-sm mb-5">{error}</p>
            <Button 
              onClick={checkStatus} 
              variant="outline"
            className="w-full h-10 border-slate-700 text-slate-300 hover:bg-[#1a2530] hover:text-white bg-transparent"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
        </div>
      </div>
    )
  }

  // Device check screen
  if (showDeviceCheck && !deviceCheckPassed) {
    return (
      <div className="min-h-screen bg-[rgb(10_15_20)] p-4">
        <div className="max-w-xl mx-auto">
          {/* Logo */}
          <div className="flex justify-center py-6 mb-2">
            <img src="/nimbus_logo_white.svg" alt="Nimbbus Providers" className="h-9" />
          </div>
          
          {/* Main Card */}
          <div className="bg-[#141c24] rounded-2xl border border-slate-700/40 overflow-hidden">
            {/* Header */}
            <div className="px-6 py-5 border-b border-slate-700/40">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#1a2530] flex items-center justify-center border border-brand-teal/30">
                  <Video className="h-5 w-5 text-brand-teal" />
                </div>
                <h1 className="text-lg font-semibold text-white">
                Prepare for Your Video Visit
                </h1>
                  </div>
                </div>
              
            {/* Content */}
            <div className="p-6">
              <DeviceCheck 
                onComplete={handleDeviceCheckComplete}
                appointmentInfo={status ? {
                  providerName: status.providerName,
                  clinicName: status.clinicName,
                  startTime: status.startTime
                } : undefined}
              />
              
              {/* Skip option */}
              {!previouslyPassed && (
                <div className="mt-5 text-center">
                  <button
                    onClick={handleDeviceCheckComplete}
                    className="text-sm text-slate-500 hover:text-brand-teal transition-colors underline underline-offset-2"
                  >
                    Skip device check (I've done this before)
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Main waiting room
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-[rgb(10_15_20)] p-4">
      {/* Logo */}
      <img src="/nimbus_logo_white.svg" alt="Nimbbus Providers" className="h-7 mb-8 md:h-9 md:mb-10" />
      
      {/* Main Card */}
      <div className="max-w-md w-full bg-[#141c24] rounded-2xl border border-slate-700/40 overflow-hidden">
        {/* Header with icon */}
        <div className="pt-6 pb-5 text-center border-b border-slate-700/40 md:pt-8 md:pb-6">
          <div className="mx-auto mb-3 w-12 h-12 bg-[#1a2530] rounded-xl flex items-center justify-center border border-brand-teal/30 md:mb-4 md:w-14 md:h-14 md:rounded-2xl">
            <Video className="h-5 w-5 text-brand-teal md:h-6 md:w-6" />
          </div>
          <h1 className="text-lg font-semibold text-white md:text-xl">Video Visit</h1>
        </div>

        <div className="p-4 space-y-4 md:p-6 md:space-y-5">
          {/* Appointment Info */}
          {status && (
            <div className="space-y-3 md:space-y-4">
              <div className="flex items-center gap-3 md:gap-4">
                <div className="w-9 h-9 rounded-lg bg-[#1a2530] flex items-center justify-center flex-shrink-0 md:w-10 md:h-10 md:rounded-xl">
                  <User className="h-4 w-4 text-slate-400 md:h-5 md:w-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-[9px] text-slate-500 uppercase tracking-wider font-medium md:text-[10px]">Provider</p>
                  <p className="text-sm text-white font-medium truncate md:text-base">{status.providerName}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 md:gap-4">
                <div className="w-9 h-9 rounded-lg bg-[#1a2530] flex items-center justify-center flex-shrink-0 md:w-10 md:h-10 md:rounded-xl">
                  <Building2 className="h-4 w-4 text-slate-400 md:h-5 md:w-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-[9px] text-slate-500 uppercase tracking-wider font-medium md:text-[10px]">Clinic</p>
                  <p className="text-sm text-white font-medium truncate md:text-base">{status.clinicName}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 md:gap-4">
                <div className="w-9 h-9 rounded-lg bg-[#1a2530] flex items-center justify-center flex-shrink-0 md:w-10 md:h-10 md:rounded-xl">
                  <Clock className="h-4 w-4 text-slate-400 md:h-5 md:w-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-[9px] text-slate-500 uppercase tracking-wider font-medium md:text-[10px]">Scheduled Time</p>
                  <p className="text-sm text-white font-medium md:text-base">{formatTime(status.startTime)}</p>
                </div>
              </div>
            </div>
          )}

          {/* Status Section */}
          <div className="pt-3 border-t border-slate-700/40 md:pt-4">
            {status?.canJoin ? (
              <div className="text-center">
                <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/25 mb-3 px-2.5 py-0.5 text-xs md:mb-4 md:px-3 md:py-1">
                  <Sparkles className="h-3 w-3 mr-1" />
                  Provider is ready
                </Badge>
                
                {autoJoinCountdown !== null && autoJoinCountdown > 0 ? (
                  <>
                    <p className="text-xs text-slate-400 mb-4 md:text-sm md:mb-5">
                      Joining automatically in <span className="text-brand-teal font-semibold">{autoJoinCountdown}</span> seconds...
                    </p>
                    <Button 
                      onClick={handleJoinClick} 
                      className="w-full h-10 text-sm bg-brand-teal hover:bg-brand-teal/90 text-white font-medium md:h-11 md:text-base" 
                    >
                      <Video className="h-4 w-4 mr-2" />
                      Join Now
                    </Button>
                    <button
                      onClick={() => setAutoJoinCountdown(null)}
                      className="mt-2 text-xs text-slate-500 hover:text-slate-300 transition-colors md:mt-3 md:text-sm"
                    >
                      Cancel auto-join
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-slate-400 mb-4 md:text-sm md:mb-5">
                      Your provider is waiting for you.
                    </p>
                    <Button 
                      onClick={handleJoinClick} 
                      className="w-full h-10 text-sm bg-brand-teal hover:bg-brand-teal/90 text-white font-medium md:h-11 md:text-base" 
                    >
                      <Video className="h-4 w-4 mr-2" />
                      Join Video Call
                    </Button>
                  </>
                )}
              </div>
            ) : (
              <div className="text-center py-2">
                <div className="flex items-center justify-center gap-2 mb-2 md:gap-2.5 md:mb-3">
                  <Loader2 className="h-4 w-4 animate-spin text-brand-teal md:h-5 md:w-5" />
                  <span className="text-sm text-white font-medium md:text-base">Waiting for provider...</span>
                </div>
                <p className="text-xs text-slate-500 md:text-sm">
                  Your provider will start the call shortly. This page will update automatically.
                </p>
              </div>
            )}
          </div>

          {/* Device check status */}
          <div className="pt-3 border-t border-slate-700/40 md:pt-4">
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-400 md:text-sm">Camera & Microphone</span>
              {deviceCheckPassed ? (
                <Badge 
                  variant="outline" 
                  className="bg-emerald-500/10 text-emerald-400 border-emerald-500/25 text-[10px] md:text-xs"
                >
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Ready
                </Badge>
              ) : (
                <button 
                  onClick={() => setShowDeviceCheck(true)}
                  className="text-xs text-brand-teal hover:text-brand-teal/80 transition-colors font-medium md:text-sm"
                >
                  Check devices
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recording Notice */}
      {transcriptionEnabled && (
        <div className="max-w-md w-full mt-3 px-4 py-3 bg-[#141c24] rounded-xl border border-slate-700/40 md:mt-4 md:px-5 md:py-3.5">
          <div className="flex items-center gap-2.5 text-xs md:gap-3 md:text-sm">
            <FileText className="h-3.5 w-3.5 text-blue-400 flex-shrink-0 md:h-4 md:w-4" />
            <span className="text-slate-400">
              This call may be recorded and transcribed for clinical documentation
            </span>
          </div>
        </div>
      )}

      {/* Consent Modal */}
      <RecordingConsentModal
        open={showConsentModal}
        onConsent={handleConsentGiven}
        onDecline={handleConsentDeclined}
        clinicName={status?.clinicName}
        providerName={status?.providerName}
        transcriptionEnabled={transcriptionEnabled}
      />
    </div>
  )
}

export default WaitingRoom
